import { debounce } from 'lodash'
import { Search } from 'lucide-react'
import React, { useMemo, useState, useRef, useEffect } from 'react'

import { Input } from 'src/components/ui'

interface SearchInputProps {
  onChange: (value: string) => void
  delay?: number
  placeholder?: string
  className?: string
  children?: React.ReactNode
}

export const SearchInput: React.FC<SearchInputProps> = ({
  onChange,
  delay = 600,
  placeholder,
  className,
  children,
}) => {
  const [inputValue, setInputValue] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  const debouncedHandleChange = useMemo(
    () =>
      debounce((newValue: string) => {
        onChange(newValue)
      }, delay),
    [onChange, delay],
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    debouncedHandleChange(newValue)
  }

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setInputValue('')
        onChange('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onChange])

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-primary" />
        <Input type="text" value={inputValue} onChange={handleChange} placeholder={placeholder} className="pl-9" />
      </div>
      {children}
    </div>
  )
}
